import React from 'react';
import {
  Box,
  Flex,
  colors,
  spacing,
  useI18nContext,
  Typography,
} from '@procore/core-react';
import PT from 'prop-types';
import styled from 'styled-components';

import { ADDITIONAL_CATEGORY_LINKS } from '@/react/consts';
import { UnstyledLink } from '@/react/styles';
import { marketplaceColors } from '@/react/styles/constants';
import { isLinkActive } from '@/react/shared/utils/helpers';

const StyledSidebar = styled(Box)`
  background-color: transparent;

  @media (max-width: 1024px) {
    padding: ${spacing.lg}px ${spacing.xl}px;
  }
`;

const MenuItem = styled(Flex).attrs({ className: 'menu-item' })`
  font-family: inherit;
  font-size: 14px;
  color: ${colors.gray15};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease-in-out 0s;
  border-radius: 0 4px 4px 0;
  padding: 6px ${spacing.sm}px 6px ${spacing.md}px;
  margin: 0;
  border-left: ${spacing.xs}px solid transparent;
  border-left-color: ${({ active }) => (active ? colors.black : 'transparent')};
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 20px;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  background-color: ${({ active }) =>
    active ? marketplaceColors.stone50 : 'transparent'};

  &:hover {
    background-color: ${marketplaceColors.stone40};
    border-left-color: ${marketplaceColors.divider};
  }

  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

const FlexMenuContainer = styled(Flex)`
  flex-direction: column;
  & > * {
    width: 100%;
  }
`;

const ListingsSidebar = ({ categories, onClose, testId = '' }) => {
  const i18n = useI18nContext();
  const testIdPrefix = testId ? `${testId}-` : '';

  return (
    <StyledSidebar>
      <FlexMenuContainer direction="column">
        {ADDITIONAL_CATEGORY_LINKS.map((category) => (
          <UnstyledLink
            data-testid={`${testIdPrefix}category-${i18n.t(category.title)}`}
            href={category.href}
            key={category.slug}
            onClick={onClose ? () => onClose() : null}
          >
            <MenuItem alignItems="center" active={isLinkActive(category.slug)}>
              {i18n.t(category.title)}
            </MenuItem>
          </UnstyledLink>
        ))}

        <Box padding="lg 0">
          <Typography intent="h2" weight="bold">
            {i18n.t('filters.categories')}
          </Typography>
        </Box>

        {categories?.map((category) => (
          <UnstyledLink
            href={`/${category.type === 'region' ? 'regions' : 'categories'}/${
              category.slug
            }`}
            key={category.slug}
            data-testid={`${testIdPrefix}category-${category.slug}`}
          >
            <MenuItem
              padding="sm xl"
              alignItems="center"
              active={isLinkActive(
                `/${category.type === 'region' ? 'regions' : 'categories'}/${
                  category.slug
                }`
              )}
            >
              {category.name}
            </MenuItem>
          </UnstyledLink>
        ))}
      </FlexMenuContainer>
    </StyledSidebar>
  );
};

ListingsSidebar.propTypes = {
  categories: PT.arrayOf(
    PT.shape({
      name: PT.string.isRequired,
      slug: PT.string.isRequired,
      type: PT.string.isRequired,
    })
  ),
  onClose: PT.func,
  testId: PT.string,
};

export default ListingsSidebar;
